import { useCallback, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { NamedItem } from "@/components/excerpts/ExcerptsBulkBar";

interface Args {
  activeWorkspaceId: string | null;
  workspaces: Array<{ id: string; workspace_label: string | null; display_name: string | null }>;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectMode: (v: boolean) => void;
  tags: NamedItem[];
  setTags: React.Dispatch<React.SetStateAction<NamedItem[]>>;
  reload: () => void;
}

/**
 * Encapsulates bulk mutation handlers + tag-create flows for the My Excerpts
 * page. Owns `bulkBusy`, `newTagName`, `newTopicName`. Caller still owns the
 * selected-set and `selectMode` flag.
 */
export function useExcerptsBulkActions({
  activeWorkspaceId,
  workspaces,
  selected,
  setSelected,
  setSelectMode,
  tags,
  setTags,
  reload,
}: Args) {
  const [bulkBusy, setBulkBusy] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTopicName, setNewTopicName] = useState("");

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, [setSelectMode, setSelected]);

  const bulkMoveToWorkspace = useCallback(
    async (workspaceId: string) => {
      if (selected.size === 0) return;
      const target = workspaces.find((w) => w.id === workspaceId);
      if (!target) return;
      const label = target.workspace_label || target.display_name || "this workspace";
      if (!confirm(`Move ${selected.size} excerpt${selected.size === 1 ? "" : "s"} to "${label}"?`)) return;
      setBulkBusy(true);
      const ids = Array.from(selected);
      const { error } = await supabase
        .from("excerpts")
        .update({ workspace_id: workspaceId })
        .in("id", ids);
      setBulkBusy(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(`Moved ${ids.length} to ${label}`);
      exitSelectMode();
      reload();
    },
    [selected, workspaces, exitSelectMode, reload],
  );

  const bulkDelete = useCallback(async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} excerpt${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const { error } = await supabase.from("excerpts").delete().in("id", ids);
    setBulkBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Deleted ${ids.length}`);
    exitSelectMode();
    reload();
  }, [selected, exitSelectMode, reload]);

  const ensureTag = useCallback(
    async (name: string): Promise<string | null> => {
      if (!activeWorkspaceId) return null;
      const trimmed = name.trim();
      if (!trimmed) return null;
      const existing = tags.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
      if (existing) return existing.id;
      const { data, error } = await supabase
        .from("tags")
        .insert({ workspace_id: activeWorkspaceId, name: trimmed })
        .select("id, name")
        .single();
      if (error || !data) {
        toast.error(error?.message ?? "Failed to create tag");
        return null;
      }
      setTags((prev) => [...prev, data as NamedItem].sort((a, b) => a.name.localeCompare(b.name)));
      return data.id;
    },
    [activeWorkspaceId, tags, setTags],
  );

  const bulkAddTag = useCallback(
    async (tagId: string) => {
      if (selected.size === 0) return;
      setBulkBusy(true);
      const rows = Array.from(selected).map((excerpt_id) => ({ excerpt_id, tag_id: tagId }));
      const { error } = await supabase
        .from("excerpt_tags")
        .upsert(rows, { onConflict: "excerpt_id,tag_id", ignoreDuplicates: true });
      setBulkBusy(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(`Tagged ${rows.length}`);
      exitSelectMode();
    },
    [selected, exitSelectMode],
  );

  const ensureTopic = useCallback(async (_name: string): Promise<string | null> => {
    toast.error("Topics are managed in Codex → Framework.");
    return null;
  }, []);

  const bulkAddTopic = useCallback(
    async (topicId: string) => {
      if (selected.size === 0) return;
      setBulkBusy(true);
      const rows = Array.from(selected).map((excerpt_id) => ({
        excerpt_id,
        topic_id: topicId,
      }));
      const { error } = await supabase
        .from("excerpt_topics")
        .upsert(rows, { onConflict: "excerpt_id,topic_id", ignoreDuplicates: true });
      setBulkBusy(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(`Tagged ${rows.length} with topic`);
      exitSelectMode();
    },
    [selected, exitSelectMode],
  );

  const handleCreateAndTag = useCallback(
    async (name: string) => {
      const id = await ensureTag(name);
      if (id) {
        setNewTagName("");
        await bulkAddTag(id);
      }
    },
    [ensureTag, bulkAddTag],
  );

  const handleCreateAndAddTopic = useCallback(
    async (name: string) => {
      const id = await ensureTopic(name);
      if (id) {
        setNewTopicName("");
        await bulkAddTopic(id);
      }
    },
    [ensureTopic, bulkAddTopic],
  );

  return {
    bulkBusy,
    newTagName,
    setNewTagName,
    newTopicName,
    setNewTopicName,
    exitSelectMode,
    bulkMoveToWorkspace,
    bulkDelete,
    bulkAddTag,
    bulkAddTopic,
    handleCreateAndTag,
    handleCreateAndAddTopic,
  };
}
